(() => {
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cleanups = new WeakMap();

  const initGallery = (root) => {
    if (!root || root.dataset.galleryInitialized === 'true') return;
    root.dataset.galleryInitialized = 'true';

    const slides = [...root.querySelectorAll('[data-gallery-slide]')];
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const dots = [...root.querySelectorAll('[data-gallery-dot]')];
    const current = root.querySelector('[data-gallery-current]');
    const prev = root.querySelector('[data-gallery-prev]');
    const next = root.querySelector('[data-gallery-next]');
    const lightbox = document.querySelector('[data-gallery-lightbox]');
    const lightboxDialog = lightbox?.querySelector('[role="dialog"]');
    const lightboxImage = lightbox?.querySelector('[data-gallery-lightbox-image]');
    const sectionId = root.closest('[id^="shopify-section-"]')?.id || `ProductGallery-${Date.now()}`;
    const galleryUid = sectionId.replace(/[^a-zA-Z0-9_-]/g, '');
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let lightboxReturnFocus = null;
    let layoutFrame = 0;
    let lastThumbIndex = -1;

    const isStacked = () => {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      return mobile ? root.dataset.mobileLayout === 'stacked' : ['stacked', 'grid'].includes(root.dataset.galleryLayout);
    };

    const setupSemantics = () => {
      slides.forEach((slide, i) => {
        slide.id = slide.id || `${galleryUid}-Panel-${i}`;
        slide.setAttribute('role', 'tabpanel');
        slide.setAttribute('tabindex', '0');
        if (thumbs[i]) {
          thumbs[i].id = thumbs[i].id || `${galleryUid}-Tab-${i}`;
          thumbs[i].setAttribute('aria-controls', slide.id);
          slide.setAttribute('aria-labelledby', thumbs[i].id);
        }
      });
    };

    const activate = (newIndex, focusTab = false) => {
      if (!slides.length) return;
      index = (newIndex + slides.length) % slides.length;
      const stacked = isStacked();

      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', stacked || active);
        slide.setAttribute('aria-hidden', stacked || active ? 'false' : 'true');
        slide.tabIndex = stacked || active ? 0 : -1;
        if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
      });

      thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
        thumb.tabIndex = active ? 0 : -1;
        if (active && (lastThumbIndex !== index || focusTab)) {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotionQuery.matches ? 'auto' : 'smooth' });
          if (focusTab) thumb.focus();
        }
      });
      lastThumbIndex = index;

      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (current) current.textContent = String(index + 1);
    };

    setupSemantics();
    const listeners = [];
    const on = (node, type, fn, options) => { if (!node) return; node.addEventListener(type, fn, options); listeners.push(() => node.removeEventListener(type, fn, options)); };

    thumbs.forEach((thumb, thumbIndex) => {
      on(thumb, 'click', () => activate(thumbIndex));
      on(thumb, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') activate(0, true);
        else if (event.key === 'End') activate(thumbs.length - 1, true);
        else activate(thumbIndex + (event.key === 'ArrowRight' ? 1 : -1), true);
      });
    });
    dots.forEach((dot) => on(dot, 'click', () => activate(Number(dot.dataset.galleryDot))));
    on(prev, 'click', () => activate(index - 1));
    on(next, 'click', () => activate(index + 1));

    let touchStartX = 0;
    const viewport = root.querySelector('.product-gallery-premium__viewport');
    on(viewport, 'touchstart', (event) => { touchStartX = event.changedTouches[0]?.clientX || 0; }, { passive: true });
    on(viewport, 'touchend', (event) => {
      if (isStacked()) return;
      const delta = (event.changedTouches[0]?.clientX || 0) - touchStartX;
      if (Math.abs(delta) > 45) activate(index + (delta < 0 ? 1 : -1));
    }, { passive: true });

    root.querySelectorAll('.product-gallery-premium__image-button').forEach((button) => on(button, 'mousemove', (event) => {
      if (!['hover', 'click_hover'].includes(root.dataset.zoomMode)) return;
      const rect = button.getBoundingClientRect();
      button.style.setProperty('--zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
      button.style.setProperty('--zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
    }));

    const updateLightbox = () => {
      const activeImage = slides[index]?.querySelector('.product-gallery-premium__image');
      if (!activeImage || !lightboxImage) return false;
      lightboxImage.src = activeImage.currentSrc || activeImage.src;
      lightboxImage.alt = activeImage.alt || '';
      return true;
    };
    const openLightbox = (trigger) => {
      if (!lightbox || !['click', 'click_hover'].includes(root.dataset.zoomMode) || !updateLightbox()) return;
      lightboxReturnFocus = trigger || document.activeElement;
      lightbox.hidden = false;
      document.body.classList.add('gallery-lightbox-open');
      lightbox.querySelector('[data-gallery-close]')?.focus();
    };
    root.querySelectorAll('[data-gallery-zoom-trigger]').forEach((trigger) => on(trigger, 'click', (event) => {
      const slide = trigger.closest('[data-gallery-slide]');
      if (slide) index = Number(slide.dataset.gallerySlide || index);
      event.preventDefault();
      openLightbox(trigger);
    }));
    const closeLightbox = () => {
      if (!lightbox || lightbox.hidden) return;
      lightbox.hidden = true;
      document.body.classList.remove('gallery-lightbox-open');
      lightboxReturnFocus?.focus?.();
    };
    lightbox?.querySelectorAll('[data-gallery-close]').forEach((button) => on(button, 'click', closeLightbox));
    on(lightbox?.querySelector('[data-gallery-lightbox-prev]'), 'click', () => { activate(index - 1); updateLightbox(); });
    on(lightbox?.querySelector('[data-gallery-lightbox-next]'), 'click', () => { activate(index + 1); updateLightbox(); });

    const onKeydown = (event) => {
      if (!lightbox || lightbox.hidden) return;
      if (event.key === 'Tab' && lightboxDialog) {
        const focusable = [...lightboxDialog.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')].filter((element) => !element.hidden);
        if (focusable.length) {
          const first = focusable[0], last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
        return;
      }
      if (event.key === 'Escape') closeLightbox();
      if (event.key === 'ArrowLeft') { activate(index - 1); updateLightbox(); }
      if (event.key === 'ArrowRight') { activate(index + 1); updateLightbox(); }
    };
    on(document, 'keydown', onKeydown);

    const onResize = () => {
      if (layoutFrame) return;
      layoutFrame = requestAnimationFrame(() => { layoutFrame = 0; activate(index); });
    };
    on(window, 'resize', onResize, { passive: true });

    const onVariantMedia = (event) => {
      const mediaId = String(event.detail?.mediaId || '');
      if (!mediaId) return;
      const target = slides.findIndex((slide) => String(slide.dataset.mediaId || '') === mediaId);
      if (target >= 0) activate(target);
    };
    on(root, 'aw:gallery:select-media', onVariantMedia);

    activate(index);
    cleanups.set(root, () => {
      listeners.forEach((off) => off());
      if (layoutFrame) cancelAnimationFrame(layoutFrame);
      document.body.classList.remove('gallery-lightbox-open');
      root.dataset.galleryInitialized = 'false';
    });
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-product-gallery]').forEach(initGallery);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true }); else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-product-gallery]').forEach((root) => { cleanups.get(root)?.(); cleanups.delete(root); }));
})();


/* Assassin Workshop — P0 PDP : synchronisation variantes sans /products/*.js */
(() => {
  'use strict';

  const q = (selector, context = document) => context.querySelector(selector);
  const qa = (selector, context = document) => [...context.querySelectorAll(selector)];

  let skuByVariantId;
  const buildSkuMap = () => {
    if (skuByVariantId) return skuByVariantId;
    skuByVariantId = new Map();

    const visit = (value) => {
      if (!value || typeof value !== 'object') return;
      if (Array.isArray(value)) {
        value.forEach(visit);
        return;
      }

      const sku = typeof value.sku === 'string' ? value.sku.trim() : '';
      const offers = value.offers;
      const offerUrl = Array.isArray(offers)
        ? offers.find((offer) => typeof offer?.url === 'string')?.url || ''
        : (typeof offers?.url === 'string' ? offers.url : '');
      const url = typeof value.url === 'string' ? value.url : offerUrl;
      if (url && sku) {
        try {
          const variantId = new URL(url, window.location.origin).searchParams.get('variant');
          if (variantId) skuByVariantId.set(String(variantId), sku);
        } catch (_) {}
      }
      Object.values(value).forEach(visit);
    };

    qa('script[type="application/ld+json"]').forEach((script) => {
      try { visit(JSON.parse(script.textContent || 'null')); } catch (_) {}
    });

    const analyticsVariants = window.ShopifyAnalytics?.meta?.product?.variants;
    if (Array.isArray(analyticsVariants)) {
      analyticsVariants.forEach((variant) => {
        const id = String(variant?.id || '');
        const sku = String(variant?.sku || '').trim();
        if (id && sku && !skuByVariantId.has(id)) skuByVariantId.set(id, sku);
      });
    }
    return skuByVariantId;
  };

  const setAvailability = (root, available) => {
    const quantity = q('.product-form input[name="quantity"]', root);
    if (quantity) {
      quantity.disabled = !available;
      quantity.setAttribute('aria-disabled', String(!available));
    }

    qa('[data-add-button]', root).forEach((button) => {
      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
    });
    qa('[data-sticky-button]').forEach((button) => {
      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
    });
    qa('[data-add-label]', root).forEach((label) => {
      label.textContent = available ? 'Ajouter au panier' : 'Épuisé';
    });
    qa('[data-sticky-label]').forEach((label) => {
      label.textContent = available ? 'Ajouter au panier' : 'Épuisé';
    });
  };

  const syncVariant = (root) => {
    const select = q('[data-variant-select]', root);
    const option = select?.options?.[select.selectedIndex];
    if (!select || !option) return;

    const priceText = option.dataset.price || '';
    const available = option.dataset.available !== 'false';
    const variantId = String(option.value || '');
    const variantTitle = option.textContent?.split('—')[0]?.trim() || '';

    const price = q('[data-product-price]', root);
    if (price && priceText) price.textContent = priceText;
    qa('[data-sticky-price]').forEach((node) => { if (priceText) node.textContent = priceText; });

    const availability = q('[data-aw-availability]', root);
    if (availability) {
      availability.textContent = available ? 'Disponible' : 'Épuisé';
      availability.classList.toggle('is-unavailable', !available);
    }

    const sku = q('[data-aw-sku]', root);
    if (sku) {
      const nextSku = buildSkuMap().get(variantId) || '';
      sku.textContent = nextSku ? `Réf. ${nextSku}` : '';
    }

    /* Le compare-at n'est pas exposé dans le select actuel : ne jamais laisser
       une remise d'une autre variante affichée après un changement. */
    const compare = q('[data-aw-compare]', root);
    if (compare) { compare.hidden = true; compare.textContent = ''; }
    const sale = q('[data-aw-sale]', root);
    if (sale) { sale.classList.remove('is-visible'); sale.textContent = ''; }

    setAvailability(root, available);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    history.replaceState({ ...history.state, variantId }, '', url);

    const mediaId = option.dataset.mediaId || '';
    if (mediaId) {
      root.dispatchEvent(new CustomEvent('aw:gallery:select-media', {
        detail: { mediaId: String(mediaId) }
      }));
    }

    const liveStatus = q('[data-aw-variant-status]', root);
    if (liveStatus) {
      liveStatus.textContent = `Variante ${variantTitle}. ${available ? 'Disponible' : 'Épuisée'}. Prix ${priceText}.`;
    }
  };

  const initVariantSync = (root) => {
    if (!root || root.dataset.awVariantSyncReady === 'true') return;
    const select = q('[data-variant-select]', root);
    root.dataset.awVariantSyncReady = 'true';

    if (!select) {
      const mainAdd = q('[data-add-button]', root);
      setAvailability(root, mainAdd ? !mainAdd.disabled : true);
      return;
    }

    const option = select.options?.[select.selectedIndex];
    if (option) setAvailability(root, option.dataset.available !== 'false');
    select.addEventListener('change', () => syncVariant(root));
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.('[data-product-root]').forEach(initVariantSync);
    if (scope.matches?.('[data-product-root]')) initVariantSync(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
